import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Printer, ArrowRight, Loader2 } from 'lucide-react';

const InvoicePrintPage = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const { API_URL, getAuthHeader } = useAuth();

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInvoice();
  }, [invoiceId]);

  const fetchInvoice = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/german-invoices/${invoiceId}`, { headers: getAuthHeader() });
      setInvoice(res.data);
    } catch (error) {
      toast.error('خطأ في تحميل الفاتورة');
      navigate('/german-invoices');
    } finally {
      setLoading(false);
    }
  };

  const fc = (n) => new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

  const getTypeLabel = (type) => {
    const labels = { rechnung: 'RECHNUNG', lieferschein: 'LIEFERSCHEIN', stornorechnung: 'STORNORECHNUNG' };
    return labels[type] || type?.toUpperCase();
  };

  const handlePrint = () => {
    const printContent = document.getElementById('invoice-content').innerHTML;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Rechnung ${invoice?.invoice_number || ''}</title>
        <style>
          @page { size: A4 portrait; margin: 12mm 15mm; }
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { 
            font-family: Arial, Helvetica, sans-serif; 
            font-size: 10pt; 
            line-height: 1.4; 
            color: #1a1a1a;
            direction: ltr;
            text-align: left;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          table { border-collapse: collapse; width: 100%; }
          th, td { padding: 2mm; }
        </style>
      </head>
      <body>
        ${printContent}
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  if (!invoice) return null;

  return (
    <div style={{ background: '#e5e7eb', minHeight: '100vh' }}>
      {/* Control Bar */}
      <div style={{ 
        position: 'fixed', top: 0, left: 0, right: 0, 
        background: 'white', padding: '12px 20px', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)', 
        zIndex: 1000,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <Button variant="outline" onClick={() => navigate('/german-invoices')} style={{ gap: '8px' }}>
          <ArrowRight style={{ width: 16, height: 16 }} />
          رجوع للفواتير
        </Button>
        <Button onClick={handlePrint} style={{ background: '#2563eb', color: 'white', gap: '8px' }}>
          <Printer style={{ width: 16, height: 16 }} />
          طباعة A4
        </Button>
      </div>

      {/* Invoice Preview */}
      <div style={{ height: '70px' }}></div>
      <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
        <div style={{ 
          width: '210mm', 
          minHeight: '297mm', 
          background: 'white', 
          padding: '15mm 20mm',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          boxSizing: 'border-box'
        }}>
          {/* This content will be printed */}
          <div id="invoice-content" style={{ fontFamily: 'Arial, Helvetica, sans-serif', fontSize: '10pt', lineHeight: '1.4', color: '#1a1a1a', direction: 'ltr', textAlign: 'left' }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12mm', paddingBottom: '6mm', borderBottom: '3px solid #1e40af' }}>
              <div style={{ maxWidth: '55%', textAlign: 'left' }}>
                {invoice.logo_base64 && (
                  <img src={invoice.logo_base64} alt="Logo" style={{ maxHeight: '22mm', maxWidth: '55mm', objectFit: 'contain', marginBottom: '4mm' }} />
                )}
                <h1 style={{ fontSize: '14pt', fontWeight: 'bold', margin: '0 0 2mm 0', color: '#1e293b' }}>{invoice.company_name || 'Firmenname'}</h1>
                <div style={{ fontSize: '9pt', color: '#475569', lineHeight: '1.5' }}>
                  {invoice.company_address && <p style={{ margin: 0, whiteSpace: 'pre-line' }}>{invoice.company_address}</p>}
                  {invoice.company_phone && <p style={{ margin: '1mm 0 0 0' }}>Tel: {invoice.company_phone}</p>}
                  {invoice.company_email && <p style={{ margin: '1mm 0 0 0' }}>E-Mail: {invoice.company_email}</p>}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <h2 style={{ fontSize: '22pt', fontWeight: 'bold', color: '#1e40af', margin: '0 0 4mm 0' }}>{getTypeLabel(invoice.invoice_type)}</h2>
                <table style={{ fontSize: '9pt', borderCollapse: 'collapse', marginLeft: 'auto' }}>
                  <tbody>
                    <tr><td style={{ padding: '1mm 4mm 1mm 0', color: '#64748b', textAlign: 'left' }}>RE-Nummer:</td><td style={{ fontWeight: '700', textAlign: 'left', fontSize: '11pt', color: '#1e293b' }}>{invoice.invoice_number}</td></tr>
                    <tr><td style={{ padding: '1mm 4mm 1mm 0', color: '#64748b', textAlign: 'left' }}>Datum:</td><td style={{ fontWeight: '600', textAlign: 'left' }}>{invoice.invoice_date}</td></tr>
                    {invoice.due_date && <tr><td style={{ padding: '1mm 4mm 1mm 0', color: '#64748b', textAlign: 'left' }}>Zahlungsziel:</td><td style={{ fontWeight: '600', textAlign: 'left' }}>{invoice.due_date}</td></tr>}
                    {invoice.customer_number && <tr><td style={{ padding: '1mm 4mm 1mm 0', color: '#64748b', textAlign: 'left' }}>Kunden-Nr.:</td><td style={{ fontWeight: '600', textAlign: 'left' }}>{invoice.customer_number}</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Customer */}
            <div style={{ marginBottom: '10mm', padding: '4mm', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '2mm', textAlign: 'left' }}>
              <p style={{ fontSize: '8pt', color: '#64748b', margin: '0 0 1mm 0' }}>Rechnungsempfänger:</p>
              <p style={{ fontSize: '11pt', fontWeight: 'bold', margin: '0 0 1mm 0', color: '#1e293b' }}>{invoice.customer_name}</p>
              <p style={{ fontSize: '9pt', margin: 0, whiteSpace: 'pre-line', color: '#475569' }}>{invoice.customer_address}</p>
            </div>

            {/* Items Table */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '8mm', fontSize: '9pt' }}>
              <thead>
                <tr>
                  <th style={{ backgroundColor: '#1e40af', color: 'white', padding: '2.5mm', textAlign: 'center', border: '1px solid #1e40af', width: '7%' }}>Pos.</th>
                  <th style={{ backgroundColor: '#1e40af', color: 'white', padding: '2.5mm', textAlign: 'left', border: '1px solid #1e40af', width: '38%' }}>Beschreibung</th>
                  <th style={{ backgroundColor: '#1e40af', color: 'white', padding: '2.5mm', textAlign: 'center', border: '1px solid #1e40af', width: '9%' }}>Menge</th>
                  <th style={{ backgroundColor: '#1e40af', color: 'white', padding: '2.5mm', textAlign: 'center', border: '1px solid #1e40af', width: '9%' }}>Einheit</th>
                  <th style={{ backgroundColor: '#1e40af', color: 'white', padding: '2.5mm', textAlign: 'right', border: '1px solid #1e40af', width: '11%' }}>Preis €</th>
                  <th style={{ backgroundColor: '#1e40af', color: 'white', padding: '2.5mm', textAlign: 'center', border: '1px solid #1e40af', width: '9%' }}>MwSt.</th>
                  <th style={{ backgroundColor: '#1e40af', color: 'white', padding: '2.5mm', textAlign: 'right', border: '1px solid #1e40af', width: '13%' }}>Betrag €</th>
                </tr>
              </thead>
              <tbody>
                {invoice.items?.map((item, idx) => (
                  <tr key={idx}>
                    <td style={{ padding: '2mm', textAlign: 'center', border: '1px solid #e2e8f0', backgroundColor: idx % 2 === 0 ? '#f8fafc' : '#fff' }}>{idx + 1}</td>
                    <td style={{ padding: '2mm', textAlign: 'left', border: '1px solid #e2e8f0', backgroundColor: idx % 2 === 0 ? '#f8fafc' : '#fff' }}>{item.description}</td>
                    <td style={{ padding: '2mm', textAlign: 'center', border: '1px solid #e2e8f0', backgroundColor: idx % 2 === 0 ? '#f8fafc' : '#fff' }}>{item.quantity}</td>
                    <td style={{ padding: '2mm', textAlign: 'center', border: '1px solid #e2e8f0', backgroundColor: idx % 2 === 0 ? '#f8fafc' : '#fff' }}>{item.unit}</td>
                    <td style={{ padding: '2mm', textAlign: 'right', border: '1px solid #e2e8f0', backgroundColor: idx % 2 === 0 ? '#f8fafc' : '#fff' }}>{fc(item.unit_price)}</td>
                    <td style={{ padding: '2mm', textAlign: 'center', border: '1px solid #e2e8f0', backgroundColor: idx % 2 === 0 ? '#f8fafc' : '#fff' }}>{item.tax_rate}%</td>
                    <td style={{ padding: '2mm', textAlign: 'right', border: '1px solid #e2e8f0', backgroundColor: idx % 2 === 0 ? '#f8fafc' : '#fff', fontWeight: '600' }}>{fc(item.gross_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Totals */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8mm' }}>
              <table style={{ width: '65mm', fontSize: '9pt' }}>
                <tbody>
                  <tr><td style={{ padding: '1.5mm 0', color: '#475569' }}>Nettobetrag:</td><td style={{ textAlign: 'right' }}>{fc(invoice.net_total)} €</td></tr>
                  {Object.entries(invoice.tax_breakdown || {}).map(([rate, amount]) => (
                    <tr key={rate}><td style={{ padding: '1.5mm 0', color: '#64748b' }}>MwSt. {rate}%:</td><td style={{ textAlign: 'right', color: '#64748b' }}>{fc(amount)} €</td></tr>
                  ))}
                  <tr><td colSpan="2" style={{ borderTop: '2px solid #1e40af', padding: '1mm 0' }}></td></tr>
                  <tr><td style={{ padding: '2.5mm 0', fontSize: '11pt', fontWeight: 'bold' }}>Gesamtbetrag:</td><td style={{ fontSize: '11pt', fontWeight: 'bold', textAlign: 'right', color: '#1e40af' }}>{fc(invoice.gross_total)} €</td></tr>
                  {invoice.paid_amount > 0 && (
                    <>
                      <tr><td style={{ color: '#16a34a' }}>Bezahlt:</td><td style={{ textAlign: 'right', color: '#16a34a' }}>-{fc(invoice.paid_amount)} €</td></tr>
                      <tr><td style={{ fontWeight: 'bold', color: '#dc2626' }}>Zu bezahlen:</td><td style={{ fontWeight: 'bold', textAlign: 'right', color: '#dc2626' }}>{fc(invoice.remaining_amount)} €</td></tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>

            {/* Bank Details */}
            {invoice.iban && (
              <div style={{ backgroundColor: '#f1f5f9', padding: '3mm', borderRadius: '2mm', marginBottom: '6mm', fontSize: '8pt' }}>
                <p style={{ fontWeight: 'bold', margin: '0 0 1.5mm 0' }}>Bankverbindung:</p>
                <span>{invoice.bank_name && `Bank: ${invoice.bank_name} | `}IBAN: {invoice.iban}{invoice.bic && ` | BIC: ${invoice.bic}`}</span>
              </div>
            )}

            {/* Notes */}
            {invoice.notes && (
              <div style={{ marginBottom: '6mm', fontSize: '9pt' }}>
                <p style={{ fontWeight: 'bold', margin: '0 0 1mm 0' }}>Bemerkung:</p>
                <p style={{ margin: 0, color: '#475569' }}>{invoice.notes}</p>
              </div>
            )}

            {/* Stamp & Signature */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '12mm', paddingTop: '6mm', borderTop: '1px dashed #cbd5e1' }}>
              <div style={{ textAlign: 'center', width: '38mm' }}>
                {invoice.stamp_base64 ? (
                  <img src={invoice.stamp_base64} alt="Stempel" style={{ maxHeight: '28mm', maxWidth: '38mm', objectFit: 'contain' }} />
                ) : (
                  <div style={{ height: '28mm', width: '38mm', border: '1px dashed #cbd5e1', borderRadius: '2mm', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '8pt' }}>Stempel</div>
                )}
                <p style={{ fontSize: '7pt', color: '#64748b', margin: '1.5mm 0 0 0' }}>Firmenstempel</p>
              </div>
              <div style={{ textAlign: 'center', width: '48mm' }}>
                {invoice.signature_base64 ? (
                  <img src={invoice.signature_base64} alt="Unterschrift" style={{ maxHeight: '22mm', maxWidth: '48mm', objectFit: 'contain' }} />
                ) : (
                  <div style={{ height: '22mm', borderBottom: '1px solid #1e293b', width: '48mm' }}></div>
                )}
                <p style={{ fontSize: '9pt', margin: '1.5mm 0 0 0', fontWeight: '500' }}>{invoice.geschaeftsfuehrer || 'Geschäftsführer'}</p>
                <p style={{ fontSize: '7pt', color: '#64748b', margin: 0 }}>Unterschrift</p>
              </div>
            </div>

            {/* Footer */}
            <div style={{ marginTop: '10mm', paddingTop: '4mm', borderTop: '1px solid #e2e8f0', textAlign: 'center' }}>
              <p style={{ fontSize: '9pt', margin: '0 0 1.5mm 0', color: '#475569' }}>{invoice.footer_text || 'Vielen Dank für Ihren Auftrag!'}</p>
              <p style={{ fontSize: '7pt', margin: 0, color: '#94a3b8' }}>
                {invoice.tax_number && `St.-Nr.: ${invoice.tax_number}`}
                {invoice.tax_number && invoice.ust_id && ' | '}
                {invoice.ust_id && `USt-IdNr.: ${invoice.ust_id}`}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoicePrintPage;
